// =============================================================================
// BP-08-05: Vendor Portal Frontend - QuickStats Component
// =============================================================================

import React from 'react';
import { Package, ShoppingCart, Star, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import type { VendorStats } from '@/features/vendor/types/vendorTypes';

interface QuickStatsProps {
  stats: VendorStats;
  isLoading?: boolean;
}

interface StatCardProps {
  label: string;
  value: string | number;
  change?: number;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
}

function StatCard({ label, value, change, icon: Icon, iconBg, iconColor }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-warm-gray mb-1">{label}</p>
          <p className="text-2xl font-display font-semibold text-navy">{value}</p>
          {change !== undefined && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm font-medium ${
                change >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {change >= 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span>{change >= 0 ? '+' : ''}{change}%</span>
              <span className="text-warm-gray font-normal">vs last month</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-8 w-24 bg-gray-200 rounded" />
          <div className="h-4 w-32 bg-gray-200 rounded" />
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}

export function QuickStats({ stats, isLoading }: QuickStatsProps) {
  const formatCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount}`;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        label="Active Products"
        value={`${stats.activeProducts} / ${stats.totalProducts}`}
        icon={Package}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
      />
      <StatCard
        label="Pending Orders"
        value={stats.pendingOrders}
        icon={ShoppingCart}
        iconBg="bg-amber-100"
        iconColor="text-amber-600"
      />
      <StatCard
        label="Average Rating"
        value={stats.avgRating.toFixed(1)}
        icon={Star}
        iconBg="bg-purple-100"
        iconColor="text-purple-600"
      />
      <StatCard
        label="Monthly Revenue"
        value={formatCurrency(stats.monthlyRevenue)}
        icon={DollarSign}
        iconBg="bg-green-100"
        iconColor="text-green-600"
      />
    </div>
  );
}

export default QuickStats;

